import { Injectable, inject } from '@angular/core';
import { QuickJSAsyncContext, QuickJSAsyncRuntime } from 'quickjs-emscripten-core';
import { Chunk } from '../../interfaces/step/step.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import { ProjectVariablesService } from '../project-variables/project-variables.service';
import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';
import { TruncationHelper } from '../../helpers/truncation.helper';
import { JavaScriptHelper } from './javascript.helper';
import { JavascriptRuntimeManager } from './javascript-runtime-manager';

export type JsRunner = {
  runtime: QuickJSAsyncRuntime;
  context: QuickJSAsyncContext;
  chunk: Chunk;
};

@Injectable({
  providedIn: 'root',
})
export class JavascriptRunnerService {
  private readonly projectVariablesService = inject(ProjectVariablesService);
  private readonly notebookVariablesService = inject(NotebookVariablesService);

  private runtimeManager = new JavascriptRuntimeManager();
  private readonly runners = new Map<number, JsRunner>();

  async ensureInitialized(): Promise<void> {
    await this.runtimeManager.initialize();
  }

  async createRunner(chunk: Chunk): Promise<JsRunner> {
    // Dispose existing runner if any
    if (this.runners.has(chunk.stepId)) {
      const existingRunner = this.runners.get(chunk.stepId)!;
      this.disposeRunner(existingRunner);
    }

    // Get healthy runtime
    const runtime = await this.runtimeManager.getHealthyRuntime();

    // Create new context from the healthy runtime
    const context = runtime.newContext();

    const runner: JsRunner = {
      runtime: runtime,
      context: context,
      chunk: chunk
    };

    this.runners.set(chunk.stepId, runner);
    return runner;
  }

  disposeRunner(runner: JsRunner): void {
    // Don't dispose context - let GC handle it like Enoch
    // This avoids "Lifetime not alive" errors from premature disposal
    // Context will be GC'd when runner map entry replaced on next execution
    // See: docs/proposals/quickjs-newpromise-migration.md
    // Note: We don't dispose runtime as it's shared
    this.runners.delete(runner.chunk.stepId);
  }

  async runScript(runner: JsRunner, script: string): Promise<any> {
    const { context, chunk } = runner;

    try {
      script = GeneralHelpers.replaceGlobals(
        script,
        this.projectVariablesService.variableList
      );

      const result = await context.evalCodeAsync(script);

      // Check context is still alive after async evaluation
      if (!context.alive) {
        console.warn(`Context was disposed during evalCodeAsync for step ${chunk.stepId}`);
        return undefined;
      }

      if (result.error) {
        throw result.error;
      }

      // Safely extract variables - context may become invalid after disposing result handles
      try {
        if (context.alive) {
          this.notebookVariablesService.addVarsFromRawToList(
            context.getProp(context.global, 'data').consume(context.dump),
            chunk as any,
            this.projectVariablesService.variableList
          );
        }
      } catch (varError: any) {
        console.warn('Failed to extract variables after script execution:', varError?.message);
      }

      return TruncationHelper.truncateOutput(JavaScriptHelper.dumpBinaryAware(context, result.value));
    } catch (error: any) {
      // Handle QuickJS context lifecycle errors gracefully
      if (error.message?.includes('Lifetime not alive') || error.message?.includes('Context was disposed')) {
        console.warn(`QuickJS context lifecycle error in runScript for step ${chunk.stepId}: ${error.message}`);
        return undefined;
      }

      const customError = new Error(error.message);
      customError.stack = error.stack;
      (customError as any).quickJSError = error;
      throw customError;
    }
  }
}
